import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import PriceFacet from './priceFacet';
import StandardFacet from './standardFacet';
import facetStyles from './facet.module.pcss';
import PlusIcon from '../../../../../../svg/icons/plus.svg';
import MinusIcon from '../../../../../../svg/icons/minus.svg';
import { FacetData, FacetValueData, MessageProperties, BreadcrumbData, PriceSliderStyle } from '../../../../types';

interface FacetProps {
  facet: FacetData;
  messages: MessageProperties;
  breadcrumbs: BreadcrumbData[];
  isExpanded: boolean;
  isDisabled: boolean;
  isIconsEnabled: boolean;
  isColoursEnabled: boolean;
  isFilterEnabled: boolean;
  plpPriceSliderStyle: PriceSliderStyle;
  facetTogglerHandler: () => void;
  facetSelectionHandler: (facetValue: FacetValueData, facetData: FacetData) => void;
}

function Facet({
  messages,
  facet,
  breadcrumbs,
  isExpanded,
  isDisabled,
  isIconsEnabled,
  isColoursEnabled,
  isFilterEnabled,
  plpPriceSliderStyle,
  facetTogglerHandler,
  facetSelectionHandler
}: FacetProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(0);

  const height = useSpring({
    height: isExpanded ? scrollHeight : 0,
    config: { duration: 350 }
  });

  useEffect(() => {
    setScrollHeight(ref.current?.scrollHeight || 0);
  });

  return (
    <div className={`${facetStyles.facet} ${isExpanded ? facetStyles['facet--expanded'] : ''}`}>
      <button
        type="button"
        aria-controls={facet.code}
        aria-expanded={isExpanded}
        disabled={isDisabled}
        onClick={facetTogglerHandler}
        className={facetStyles['facet__title']}
      >
        <span className={facetStyles['facet__title-text']}>{facet.name}</span>
        {isExpanded ? (
          <MinusIcon className={facetStyles['facet__icon']} />
        ) : (
          <PlusIcon className={facetStyles['facet__icon']} />
        )}
        {breadcrumbs.length > 0 && (
          <ul className={facetStyles['facet__title-applied-facets']}>
            {breadcrumbs
              .filter(({ facetCode }) => facetCode === facet.code)
              .map(({ facetCode, facetValueName, facetValueNameMobile }) => (
                <li key={`${facetCode}_${facetValueName}`} className={facetStyles['facet__title-applied-facets-item']}>
                  {facetValueNameMobile?.toLowerCase() || facetValueName?.toLowerCase()}
                </li>
              ))}
          </ul>
        )}
      </button>
      <animated.div ref={ref} style={height} id={facet.code} className={facetStyles['facet__wrapper']}>
        <div className={facetStyles['facet__wrapper-content']}>
          {facet.priceFacet ? (
            <PriceFacet
              facet={facet}
              isDisabled={isDisabled}
              isExpanded={isExpanded}
              sliderStyle={plpPriceSliderStyle}
              facetSelectionHandler={facetSelectionHandler}
            />
          ) : (
            <StandardFacet
              facet={facet}
              messages={messages}
              isDisabled={isDisabled}
              isIconsEnabled={isIconsEnabled}
              isColoursEnabled={isColoursEnabled}
              isFilterEnabled={isFilterEnabled}
              facetSelectionHandler={facetSelectionHandler}
            />
          )}
        </div>
      </animated.div>
    </div>
  );
}

export default Facet;
